/**
 * Runtime config powered by next-runtime-env.
 *
 * PublicEnvScript (added to RootLayout) injects all NEXT_PUBLIC_* env vars
 * into window.__ENV at server render time. The env() helper then reads from
 * window.__ENV on the client and from process.env on the server — no build-
 * time inlining, same image works across dev / sit / stage / prod / us / eu.
 */

import { env } from "next-runtime-env";

export interface RuntimeConfig {
  region: string;
  authority: string;
  clientId: string;
  cognitoDomain: string;
  redirectUri: string;
  logoutUri: string;
  scope: string;
  assetsUrl: string;
  filesUrl: string;
  assessmentsUrl: string;
  careplansUrl: string;
  orgsUrl: string;
  usersUrl: string;
  // Same-origin GraphQL paths (e.g. "/graphql/users") used for queries and
  // mutations when the CloudFront AppSync proxy is enabled. Empty means the
  // proxy is off and the raw *Url above is used for HTTP as well.
  //
  // These exist separately rather than replacing the *Url fields because the
  // raw AppSync URL is still needed: appsync-realtime.ts derives the wss://
  // subscription endpoint and its auth `host` from it by string replacement.
  filesHttpUrl: string;
  assessmentsHttpUrl: string;
  assetsHttpUrl: string;
  careplansHttpUrl: string;
  orgsHttpUrl: string;
  usersHttpUrl: string;
  onetrustDomainScript: string;
  doNotShowOnetrust: boolean;
  googleIdpName: string;
  /** CloudWatch RUM app monitor UUID. Empty disables RUM entirely. */
  rumAppMonitorId: string;
  /** AWS region of the app monitor — the data-residency boundary. */
  rumRegion: string;
  /** Frontend image tag, reported as the RUM application version. */
  rumAppVersion: string;
  /** Must match the app monitor resource policy's rum:alias condition. */
  rumAlias: string;
  /** Portion of sessions RUM collects, 0-1. */
  rumSessionSampleRate: number;
}

export function getRuntimeConfig(): RuntimeConfig {
  return {
    region: env("NEXT_PUBLIC_REGION") ?? "",
    authority: env("NEXT_PUBLIC_AUTHORITY") ?? "",
    clientId: env("NEXT_PUBLIC_CLIENTID") ?? "",
    cognitoDomain: env("NEXT_PUBLIC_COGNITO_DOMAIN") ?? "",
    redirectUri: env("NEXT_PUBLIC_REDIRECT_URI") ?? "",
    logoutUri: env("NEXT_PUBLIC_LOGOUT_URI") ?? "",
    scope: env("NEXT_PUBLIC_SCOPE") ?? "",
    assetsUrl: env("NEXT_PUBLIC_ASSETS_APPSYNC_URL") ?? "",
    filesUrl: env("NEXT_PUBLIC_FILES_APPSYNC_URL") ?? "",
    assessmentsUrl: env("NEXT_PUBLIC_ASSESSMENTS_APPSYNC_URL") ?? "",
    careplansUrl: env("NEXT_PUBLIC_CAREPLANS_APPSYNC_URL") ?? "",
    orgsUrl: env("NEXT_PUBLIC_ORGANIZATIONS_APPSYNC_URL") ?? "",
    usersUrl: env("NEXT_PUBLIC_USERS_APPSYNC_URL") ?? "",
    filesHttpUrl: env("NEXT_PUBLIC_FILES_APPSYNC_HTTP_URL") ?? "",
    assessmentsHttpUrl: env("NEXT_PUBLIC_ASSESSMENTS_APPSYNC_HTTP_URL") ?? "",
    assetsHttpUrl: env("NEXT_PUBLIC_ASSETS_APPSYNC_HTTP_URL") ?? "",
    careplansHttpUrl: env("NEXT_PUBLIC_CAREPLANS_APPSYNC_HTTP_URL") ?? "",
    orgsHttpUrl: env("NEXT_PUBLIC_ORGANIZATIONS_APPSYNC_HTTP_URL") ?? "",
    usersHttpUrl: env("NEXT_PUBLIC_USERS_APPSYNC_HTTP_URL") ?? "",
    onetrustDomainScript: env("NEXT_PUBLIC_ONETRUST_DOMAIN_SCRIPT") ?? "",
    doNotShowOnetrust: env("NEXT_PUBLIC_DO_NOT_SHOW_ONETRUST") === "true",
    // Native "Google" where IdP MFA isn't enforced, generic-OIDC
    // "Google-Account" where it is — set per region from cognito state.
    //
    // Deliberately NOT defaulted to "Google": in an enforcing region the
    // provider is "Google-Account", so guessing "Google" would deep-link
    // identity_provider at a provider that isn't on the app client and break
    // "Retry with Google" on the one screen that needs it. Empty means
    // "unknown", and callers fall back to plain managed login (one extra tap,
    // but it works). See lib/auth/idpProviders.ts.
    googleIdpName: env("NEXT_PUBLIC_GOOGLE_IDP_NAME") ?? "",

    // CloudWatch RUM. All set per regional deployment by
    // iac/runway/nextjs-lambda.tf, so the US Lambda serves a US app monitor
    // and the EU Lambda an EU one from the same image.
    //
    // rumAppMonitorId is the kill switch: Terraform's rum_enabled = false
    // leaves it empty and initRum() no-ops.
    rumAppMonitorId: env("NEXT_PUBLIC_RUM_APP_MONITOR_ID") ?? "",
    rumRegion: env("NEXT_PUBLIC_RUM_REGION") ?? "",
    rumAppVersion: env("NEXT_PUBLIC_RUM_APP_VERSION") ?? "unknown",
    rumAlias: env("NEXT_PUBLIC_RUM_ALIAS") ?? "",
    // Number(undefined) is NaN, which RUM would treat as an invalid rate —
    // fall back to 1 (collect everything) rather than silently sampling out.
    rumSessionSampleRate: Number(
      env("NEXT_PUBLIC_RUM_SESSION_SAMPLE_RATE") ?? "1",
    ),
  };
}

/** @deprecated Use getRuntimeConfig() directly. Kept for call-site compatibility. */
export const readServerConfig = getRuntimeConfig;
